import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import StrategyCard from '../../components/trading/StrategyCard';
import EmptyState from '../../components/common/EmptyState';
import CopySettingsDialog, {
  CopySettings,
} from '../../components/trading/CopySettingsDialog';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import TradingFilters, {
  RiskFilter,
} from '../../components/trading/TradingFilters';
import PageHeader from '../../components/common/PageHeader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Search } from 'lucide-react';
import toast from 'react-hot-toast';

export type TraderStrategy = {
  id: string;
  name: string;
  strategyName: string;
  roi: number;
  winRate: number;
  totalProfit: number;
  aum: number;
  followers: number;
  riskLevel: 'low' | 'medium' | 'high';
  riskScore: number;
  status: 'active' | 'inactive';
  minCopyAmount: number;
  commissionRate: number;
  description: string;
};

const defaultSettings: CopySettings = {
  copyAmount: '',
  allocationType: 'proportional',
  fixedLot: '0.10',
  riskMultiplier: '1.0',
  maxDrawdown: '20',
  maxDailyLoss: '100',
  maxOpenTrades: '5',
  copyOpenTrades: false,
  reverseCopy: false,
  slippagePips: '3',
  stopCopyLevel: '',
};

export const MOCK_TRADERS: TraderStrategy[] = [
  {
    id: 'TRD001',
    name: 'John Smith',
    strategyName: 'Gold Scalper Pro',
    roi: 42.5,
    winRate: 78.4,
    totalProfit: 56875,
    aum: 125000,
    followers: 45,
    riskLevel: 'high',
    riskScore: 8,
    status: 'active',
    minCopyAmount: 100,
    commissionRate: 15,
    description: 'High-frequency gold trading strategy with consistent returns',
  },
  {
    id: 'TRD002',
    name: 'Jane Doe',
    strategyName: 'Forex Trend Master',
    roi: 35.8,
    winRate: 82.1,
    totalProfit: 35084,
    aum: 98000,
    followers: 38,
    riskLevel: 'medium',
    riskScore: 5,
    status: 'active',
    minCopyAmount: 50,
    commissionRate: 12,
    description: 'Trend-following strategy across major currency pairs',
  },
  {
    id: 'TRD003',
    name: 'Bob Johnson',
    strategyName: 'Conservative Growth',
    roi: 18.2,
    winRate: 89.5,
    totalProfit: 13650,
    aum: 75000,
    followers: 22,
    riskLevel: 'low',
    riskScore: 3,
    status: 'active',
    minCopyAmount: 25,
    commissionRate: 8,
    description: 'Low-risk strategy focused on steady capital growth',
  },
  {
    id: 'TRD004',
    name: 'Mike Wilson',
    strategyName: 'Crypto Momentum',
    roi: 52.3,
    winRate: 71.0,
    totalProfit: 78450,
    aum: 150000,
    followers: 55,
    riskLevel: 'high',
    riskScore: 9,
    status: 'active',
    minCopyAmount: 200,
    commissionRate: 20,
    description: 'Aggressive cryptocurrency momentum trading strategy',
  },
];

const CopyTradingMarketplace = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [riskFilter, setRiskFilter] = useState<RiskFilter>('all');
  const [sortBy, setSortBy] = useState<'roi' | 'followers' | 'aum' | 'risk'>(
    'roi'
  );
  const [selectedTrader, setSelectedTrader] = useState<TraderStrategy | null>(
    null
  );
  const [copySettings, setCopySettings] =
    useState<CopySettings>(defaultSettings);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filteredTraders = MOCK_TRADERS.filter(trader => {
    const matchesSearch =
      searchTerm === '' ||
      trader.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      trader.strategyName.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRisk = riskFilter === 'all' || trader.riskLevel === riskFilter;

    return matchesSearch && matchesRisk && trader.status === 'active';
  }).sort((a, b) => {
    if (sortBy === 'roi') return b.roi - a.roi;
    if (sortBy === 'followers') return b.followers - a.followers;
    if (sortBy === 'aum') return b.aum - a.aum;
    if (sortBy === 'risk') return a.riskScore - b.riskScore;
    return 0;
  });

  const handleOpenCopyDialog = (trader: TraderStrategy) => {
    setSelectedTrader(trader);
    setCopySettings({
      ...defaultSettings,
      copyAmount: String(trader.minCopyAmount),
    });
    setShowCopyDialog(true);
  };

  const handleViewDetails = (traderId: string) => {
    navigate(`/trading-services/copy-trading/strategy/${traderId}`);
  };

  const handleConfirmCopySettings = () => {
    if (!selectedTrader) return;

    const amount = Number(copySettings.copyAmount);
    if (!amount || amount < selectedTrader.minCopyAmount) {
      toast.error(`Minimum copy amount is $${selectedTrader.minCopyAmount}`);
      return;
    }

    setShowCopyDialog(false);
    setShowConfirmDialog(true);
  };

  const handleStartCopying = async () => {
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 800));
    setIsSubmitting(false);
    setShowConfirmDialog(false);
    toast.success(
      `Successfully started copying ${selectedTrader?.strategyName}`
    );
    setSelectedTrader(null);
  };

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Strategy Marketplace"
          description="Discover master traders, review performance metrics, and configure automated copy trading."
        />
      </div>

      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
        <div className="flex-1">
          <TradingFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search strategies or traders..."
            riskFilter={riskFilter}
            onRiskFilterChange={setRiskFilter}
          />
        </div>
        <div className="w-full md:w-48">
          <Select value={sortBy} onValueChange={(val: any) => setSortBy(val)}>
            <SelectTrigger>
              <SelectValue placeholder="Sort By" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="roi">Highest ROI</SelectItem>
              <SelectItem value="followers">Most Followers</SelectItem>
              <SelectItem value="aum">Highest AUM</SelectItem>
              <SelectItem value="risk">Lowest Risk Score</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {filteredTraders.map(trader => (
          <StrategyCard
            key={trader.id}
            id={trader.id}
            name={trader.strategyName}
            manager={trader.name}
            roi={trader.roi}
            winRate={trader.winRate}
            totalProfit={trader.totalProfit}
            totalInvested={trader.aum}
            investors={trader.followers}
            riskLevel={trader.riskLevel}
            riskScore={trader.riskScore}
            status={trader.status}
            minCopyAmount={trader.minCopyAmount}
            description={trader.description}
            actionLabel="Copy Strategy"
            onView={() => handleViewDetails(trader.id)}
            onInvest={() => handleOpenCopyDialog(trader)}
          />
        ))}
      </div>

      {filteredTraders.length === 0 && (
        <Card>
          <CardContent className="py-12">
            <EmptyState
              icon={Search}
              title="No strategies found"
              description="Try adjusting your search query, sorting, or risk filters to discover active master traders."
            />
          </CardContent>
        </Card>
      )}

      {selectedTrader && (
        <CopySettingsDialog
          open={showCopyDialog}
          onOpenChange={setShowCopyDialog}
          traderName={selectedTrader.name}
          strategyName={selectedTrader.strategyName}
          riskScore={selectedTrader.riskScore}
          minCopyAmount={selectedTrader.minCopyAmount}
          commissionRate={selectedTrader.commissionRate}
          settings={copySettings}
          onSettingsChange={setCopySettings}
          onConfirm={handleConfirmCopySettings}
        />
      )}

      <ConfirmationModal
        isOpen={showConfirmDialog}
        title="Confirm Copy Trading Subscription"
        message={`Start copying ${selectedTrader?.strategyName} by ${selectedTrader?.name} with $${copySettings.copyAmount}?`}
        confirmText="Start Copying"
        onConfirm={handleStartCopying}
        onCancel={() => setShowConfirmDialog(false)}
        type="info"
        loading={isSubmitting}
        loadingText="Setting up copy trade..."
      />
    </div>
  );
};

export default CopyTradingMarketplace;
